import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  File,
  FileImage,
  FileText,
  FolderClosed,
  FolderOpen,
  FolderPlus,
  LayoutTemplate,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  LoaderCircle,
  Save,
  Trash2,
  Upload
} from "lucide-react";

import type { BrowserEntry, CompanyScope } from "@/features/app/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { MarkdownEditor } from "@/components/markdown-editor";
import { Progress } from "@/components/ui/progress";
export { TendersPage } from "./tenders-page";

type FileTreeNode = {
  name: string;
  path: string;
  type: "file" | "directory";
  isMarkdown: boolean;
  isImage: boolean;
  children: FileTreeNode[];
};

const buildFileTree = (entries: BrowserEntry[]): FileTreeNode[] => {
  const root: FileTreeNode[] = [];
  const directoryMap = new Map<string, FileTreeNode>();

  const sortedEntries = [...entries].sort((a, b) => {
    if (a.type !== b.type) return a.type === "directory" ? -1 : 1;
    return a.path.localeCompare(b.path);
  });

  for (const entry of sortedEntries) {
    if (entry.type === "directory") {
      const node: FileTreeNode = {
        name: entry.path.split("/").pop() ?? entry.path,
        path: entry.path,
        type: "directory",
        isMarkdown: false,
        isImage: false,
        children: []
      };
      directoryMap.set(entry.path, node);

      const parentPath = entry.path.split("/").slice(0, -1).join("/");
      const parent = parentPath ? directoryMap.get(parentPath) : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        root.push(node);
      }
    }
  }

  for (const entry of sortedEntries) {
    if (entry.type === "file") {
      const node: FileTreeNode = {
        name: entry.path.split("/").pop() ?? entry.path,
        path: entry.path,
        type: "file",
        isMarkdown: entry.isMarkdown,
        isImage: entry.isImage,
        children: []
      };

      const parts = entry.path.split("/");
      const parentPath = parts.slice(0, -1).join("/");
      const parent = parentPath ? directoryMap.get(parentPath) : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        root.push(node);
      }
    }
  }

  return root;
};

type FileTreeItemProps = {
  node: FileTreeNode;
  depth: number;
  selectedPath: string;
  onSelectPath: (path: string) => void;
  onRequestDeleteFile: (path: string) => void;
};

const FileTreeItem = ({ node, depth, selectedPath, onSelectPath, onRequestDeleteFile }: FileTreeItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSelected = node.type === "file" && selectedPath === node.path;
  const paddingLeft = depth * 16 + 8;

  useEffect(() => {
    if (node.type !== "directory" || !selectedPath) {
      return;
    }
    if (selectedPath === node.path || selectedPath.startsWith(`${node.path}/`)) {
      setIsOpen(true);
    }
  }, [node.path, node.type, selectedPath]);

  if (node.type === "directory") {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="group flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-sm hover:bg-muted/50"
            style={{ paddingLeft }}
          >
            {isOpen ? <ChevronDown className="size-3.5 shrink-0" /> : <ChevronRight className="size-3.5 shrink-0" />}
            {isOpen ? (
              <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
            ) : (
              <FolderClosed className="size-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate font-medium">{node.name}</span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {node.children.map((child) => (
            <FileTreeItem
              key={`${child.type}:${child.path}`}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelectPath={onSelectPath}
              onRequestDeleteFile={onRequestDeleteFile}
            />
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  const FileIcon = node.isMarkdown ? FileText : node.isImage ? FileImage : File;

  return (
    <div className="group relative flex items-center">
      <button
        type="button"
        className={`flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-sm transition-colors ${
          isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted/60"
        }`}
        style={{ paddingLeft: paddingLeft + 18 }}
        onClick={() => onSelectPath(node.path)}
      >
        <FileIcon className={`size-4 shrink-0 ${isSelected ? "text-primary-foreground" : "text-muted-foreground"}`} />
        <span className="truncate">{node.name}</span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`absolute right-1 rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 ${
              isSelected ? "text-primary-foreground hover:bg-primary-foreground/20" : "text-muted-foreground hover:bg-muted"
            }`}
          >
            <MoreHorizontal className="size-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-36">
          <DropdownMenuItem variant="destructive" onClick={() => onRequestDeleteFile(node.path)}>
            <Trash2 className="size-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

type PlaybookPageProps = {
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<void>;
  statusMessage: string;
};

export const PlaybookPage = ({ value, onChange, onSave, statusMessage }: PlaybookPageProps) => (
  <div className="flex h-[calc(100svh-140px)] flex-col gap-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm">Your team's writing guide — tone, structure, and quality standards applied to every proposal.</p>
      {statusMessage ? <Badge variant="secondary">{statusMessage}</Badge> : null}
    </div>

    <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b px-4 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">playbook.md</span>
          <Button size="sm" onClick={() => void onSave()}>
            <Save className="size-3.5" />
            Save
          </Button>
        </div>
        <div className="min-h-0 flex-1">
          <MarkdownEditor
            className="h-full"
            chrome="embedded"
            fillHeight
            value={value}
            onChange={onChange}
            placeholderText="Write your team's proposal guidelines in markdown..."
          />
        </div>
      </div>
    </div>
  </div>
);

type PromptTemplatePageProps = {
  fileName: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<void>;
  statusMessage: string;
  placeholder: string;
  saveLabel?: string;
};

export const PromptTemplatePage = ({
  fileName,
  description,
  value,
  onChange,
  onSave,
  statusMessage,
  placeholder,
  saveLabel = "Save"
}: PromptTemplatePageProps) => (
  <div className="flex h-[calc(100svh-140px)] flex-col gap-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm">{description}</p>
      {statusMessage ? <Badge variant="secondary">{statusMessage}</Badge> : null}
    </div>

    <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b px-4 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{fileName}</span>
          <Button size="sm" onClick={() => void onSave()}>
            <Save className="size-3.5" />
            {saveLabel}
          </Button>
        </div>
        <div className="min-h-0 flex-1">
          <MarkdownEditor
            className="h-full"
            chrome="embedded"
            fillHeight
            value={value}
            onChange={onChange}
            placeholderText={placeholder}
          />
        </div>
      </div>
    </div>
  </div>
);

type DocumentLibraryPageProps = {
  scope: CompanyScope;
  description: string;
  importActivity: {
    status: "idle" | "running" | "succeeded" | "failed";
    title: string;
    detail: string;
    progress: number;
    error: string | null;
  };
  entries: BrowserEntry[];
  selectedPath: string;
  onSelectPath: (path: string) => void;
  selectedEntry: BrowserEntry | null;
  editorValue: string;
  onChangeEditor: (value: string) => void;
  onSaveMarkdown: () => Promise<void>;
  createDirectory: string;
  onChangeCreateDirectory: (value: string) => void;
  createFileName: string;
  onChangeCreateFileName: (value: string) => void;
  onCreateMarkdownFile: () => Promise<void>;
  uploadDirectory: string;
  onChangeUploadDirectory: (value: string) => void;
  onImportFiles: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onDeleteFile: (path: string) => Promise<boolean>;
  statusMessage: string;
};

export const DocumentLibraryPage = ({
  scope,
  description,
  importActivity,
  entries,
  selectedPath,
  onSelectPath,
  selectedEntry,
  editorValue,
  onChangeEditor,
  onSaveMarkdown,
  createDirectory,
  onChangeCreateDirectory,
  createFileName,
  onChangeCreateFileName,
  onCreateMarkdownFile,
  uploadDirectory,
  onChangeUploadDirectory,
  onImportFiles,
  onDeleteFile,
  statusMessage
}: DocumentLibraryPageProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingDeletePath, setPendingDeletePath] = useState("");
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const fileTree = useMemo(() => buildFileTree(entries), [entries]);
  const fileCount = entries.filter((e) => e.type === "file").length;
  const imageUrl =
    selectedEntry?.isImage && selectedPath
      ? `/api/company/file/raw?scope=${scope}&path=${encodeURIComponent(selectedPath)}`
      : null;
  const ImportStatusIcon =
    importActivity.status === "running"
      ? LoaderCircle
      : importActivity.status === "failed"
        ? AlertCircle
        : CheckCircle2;
  const deleteTargetLabel = pendingDeletePath.split("/").pop() ?? pendingDeletePath;

  const confirmDeleteFile = async (): Promise<void> => {
    if (!pendingDeletePath) return;
    setIsDeletingFile(true);
    const didDelete = await onDeleteFile(pendingDeletePath);
    setIsDeletingFile(false);
    if (!didDelete) return;
    setPendingDeletePath("");
  };

  return (
    <div className="flex h-[calc(100svh-140px)] flex-col gap-4">
      <AlertDialog
        open={Boolean(pendingDeletePath)}
        onOpenChange={(open) => {
          if (!open && !isDeletingFile) {
            setPendingDeletePath("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTargetLabel
                ? `This permanently removes ${deleteTargetLabel} from the library.`
                : "This permanently removes the selected file from the library."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingFile}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingFile}
              onClick={(event) => {
                event.preventDefault();
                void confirmDeleteFile();
              }}
            >
              {isDeletingFile ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">{description}</p>
        {statusMessage ? <Badge variant="secondary">{statusMessage}</Badge> : null}
      </div>

      {importActivity.status !== "idle" ? (
        <Card className={importActivity.status === "failed" ? "border-destructive/40" : undefined}>
          <CardContent className="flex flex-col gap-3 px-4 py-4">
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 rounded-full p-2 ${
                  importActivity.status === "failed"
                    ? "bg-destructive/10 text-destructive"
                    : importActivity.status === "succeeded"
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                <ImportStatusIcon className={`size-4 ${importActivity.status === "running" ? "animate-spin" : ""}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium">{importActivity.title}</p>
                  <Badge variant={importActivity.status === "failed" ? "destructive" : "outline"}>
                    {importActivity.status === "running"
                      ? "Importing"
                      : importActivity.status === "failed"
                        ? "Failed"
                        : "Complete"}
                  </Badge>
                </div>
                <p className="text-muted-foreground mt-1 text-sm">{importActivity.detail}</p>
                {importActivity.error ? <p className="text-destructive mt-2 text-sm">{importActivity.error}</p> : null}
              </div>
              <span className="text-muted-foreground text-xs tabular-nums">{importActivity.progress}%</span>
            </div>
            <Progress value={importActivity.progress} className="h-2" />
          </CardContent>
        </Card>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-0 overflow-hidden rounded-lg border">
        {sidebarOpen && (
          <div className="flex w-72 shrink-0 flex-col border-r bg-muted/15">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-medium">
                Files{fileCount > 0 ? ` (${fileCount})` : ""}
              </span>
              <div className="flex items-center gap-1">
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-7" aria-label="New markdown file">
                      <Plus className="size-3.5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create markdown file</DialogTitle>
                      <DialogDescription>Add a new document to this library.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <Input
                        placeholder="Folder (optional)"
                        value={createDirectory}
                        onChange={(e) => onChangeCreateDirectory(e.target.value)}
                      />
                      <Input
                        placeholder="File name, e.g. overview.md"
                        value={createFileName}
                        onChange={(e) => onChangeCreateFileName(e.target.value)}
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        onClick={() => {
                          void onCreateMarkdownFile();
                          setShowCreateDialog(false);
                        }}
                      >
                        <FolderPlus className="size-4" />
                        Create
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-7" aria-label="Upload file">
                      <Upload className="size-3.5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Import documents</DialogTitle>
                      <DialogDescription>Upload a zip archive or a single document and convert it into markdown.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <Input
                        placeholder="Folder (optional)"
                        value={uploadDirectory}
                        onChange={(e) => onChangeUploadDirectory(e.target.value)}
                      />
                      <Input
                        type="file"
                        accept=".zip,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.md,.markdown,.txt,.json,.csv,.xml,.html,.htm,.graphql,image/*"
                        onChange={(e) => {
                          void onImportFiles(e);
                          setShowUploadDialog(false);
                        }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="size-3.5" />
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="space-y-0.5 p-2">
                {fileTree.length === 0 ? (
                  <p className="text-muted-foreground px-2 py-6 text-center text-sm">
                    No files yet. Create a markdown file or import a zip or single document to get started.
                  </p>
                ) : (
                  fileTree.map((node) => (
                    <FileTreeItem
                      key={`${node.type}:${node.path}`}
                      node={node}
                      depth={0}
                      selectedPath={selectedPath}
                      onSelectPath={onSelectPath}
                      onRequestDeleteFile={setPendingDeletePath}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2 border-b px-4 py-2">
            {!sidebarOpen && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => setSidebarOpen(true)}
                aria-label="Show sidebar"
              >
                <PanelLeftOpen className="size-3.5" />
              </Button>
            )}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {selectedPath ? (
                <>
                  <span className="truncate text-sm font-medium">{selectedPath}</span>
                  {selectedEntry?.isMarkdown && (
                    <Badge variant="outline" className="shrink-0">Markdown</Badge>
                  )}
                  {selectedEntry?.isImage && (
                    <Badge variant="outline" className="shrink-0">Image</Badge>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground text-sm">No file selected</span>
              )}
            </div>
            {selectedEntry?.isMarkdown && (
              <Button size="sm" onClick={() => void onSaveMarkdown()}>
                <Save className="size-3.5" />
                Save
              </Button>
            )}
            {selectedEntry && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setPendingDeletePath(selectedEntry.path)}
              >
                <Trash2 className="size-3.5" />
                Delete
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-auto">
            {selectedEntry?.isMarkdown ? (
              <MarkdownEditor
                className="h-full"
                chrome="embedded"
                fillHeight
                value={editorValue}
                onChange={onChangeEditor}
                placeholderText="Start writing markdown..."
              />
            ) : selectedEntry?.isImage && imageUrl ? (
              <div className="flex h-full items-center justify-center bg-muted/15 p-8">
                <img
                  src={imageUrl}
                  alt={selectedPath}
                  className="max-h-full max-w-full rounded-lg object-contain"
                />
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
                <LayoutTemplate className="text-muted-foreground size-10" />
                <div className="text-center">
                  <p className="text-sm font-medium">Choose a document from the file browser</p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Markdown files open in the editor and images render as previews.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type SettingsPageProps = {
  availableModels: readonly string[];
  selectedModel: string;
  onSelectModel: (value: string) => void;
  onSave: () => Promise<void>;
  statusMessage: string;
};

export const SettingsPage = ({
  availableModels,
  selectedModel,
  onSelectModel,
  onSave,
  statusMessage
}: SettingsPageProps) => (
  <div className="space-y-6">
    <div className="flex flex-wrap items-center justify-end gap-2">
      {statusMessage ? <Badge variant="secondary">{statusMessage}</Badge> : null}
      <Button onClick={() => void onSave()}>
        <Save className="size-4" />
        Save settings
      </Button>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Default workflow model</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {availableModels.map((model) => {
          const isSelected = model === selectedModel;
          return (
            <Button
              key={model}
              variant={isSelected ? "default" : "outline"}
              onClick={() => onSelectModel(model)}
            >
              {model}
            </Button>
          );
        })}
      </CardContent>
    </Card>
  </div>
);
